import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '../services/auth';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Users, DollarSign, ChevronDown, ChevronRight, ArrowRightLeft } from 'lucide-react';

const PROXY = '/.netlify/functions/authnet-vip';

function fmt$(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtN(n) { return Number(n || 0).toLocaleString(); }
function fmtTime(t) {
  if (!t) return '';
  const d = new Date(t);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

async function postApi(action, extra = {}) {
  const res = await fetch(PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...extra }),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

export default function VipHealth() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [checkingVoids, setCheckingVoids] = useState(false);
  const [voidResult, setVoidResult] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('drift');
  const [txns, setTxns] = useState([]);
  const [txnFilter, setTxnFilter] = useState('all');
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnPage, setTxnPage] = useState(1);
  const [txnType, setTxnType] = useState(null);
  const PAGE_SIZE = 100;
  const [notAttempted, setNotAttempted] = useState([]);
  const [naLoading, setNaLoading] = useState(false);
  const [naTotal, setNaTotal] = useState(0);
  const [naPage, setNaPage] = useState(1);
  const [expandedDupes, setExpandedDupes] = useState(new Set());

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await postApi('report');
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await postApi('sync');
      setSyncResult(result);
      await loadReport();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleCheckVoids = async () => {
    setCheckingVoids(true);
    setVoidResult(null);
    setError(null);
    try {
      // Keep calling until done (processes 200 per call)
      let totalVoided = 0;
      let totalChecked = 0;
      let done = false;
      while (!done) {
        const result = await postApi('check-voids');
        totalVoided += (result.voided || 0);
        totalChecked += (result.checked || 0);
        done = result.done;
      }
      setVoidResult({ checked: totalChecked, voided: totalVoided });
      await loadReport();
    } catch (e) {
      setError(e.message);
    } finally {
      setCheckingVoids(false);
    }
  };

  const loadTransactions = useCallback(async (filter, page) => {
    setTxnLoading(true);
    try {
      const data = await postApi('transactions', { filter: filter === 'all' ? undefined : filter, page, pageSize: PAGE_SIZE });
      setTxns(data.transactions || []);
      setTxnTotal(data.total || 0);
      setTxnType(data.type || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setTxnLoading(false);
    }
  }, []);

  const loadNotAttempted = useCallback(async (page) => {
    setNaLoading(true);
    try {
      const data = await postApi('transactions', { filter: 'not-attempted', page, pageSize: PAGE_SIZE });
      setNotAttempted(data.transactions || []);
      setNaTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setNaLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'transactions') loadTransactions(txnFilter, txnPage);
  }, [tab, txnFilter, txnPage, loadTransactions]);

  useEffect(() => {
    if (tab === 'not-attempted') loadNotAttempted(naPage);
  }, [tab, naPage, loadNotAttempted]);

  const handleFilterChange = (f) => {
    setTxnFilter(f);
    setTxnPage(1);
  };

  const toggleDupe = (custId) => {
    setExpandedDupes(prev => {
      const next = new Set(prev);
      next.has(custId) ? next.delete(custId) : next.add(custId);
      return next;
    });
  };

  return (
    <div className="page-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>VIP Renewal Health</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-outline"
            onClick={handleCheckVoids}
            disabled={checkingVoids || syncing}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={16} className={checkingVoids ? 'spinning' : ''} />
            {checkingVoids ? 'Checking Auth.net...' : 'Check Voids'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing || checkingVoids}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={16} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Syncing...' : 'Sync from Backend'}
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ borderLeft: '4px solid #e53e3e', marginBottom: 16 }}>
          <div className="card-body" style={{ color: '#e53e3e' }}>
            <AlertTriangle size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {error}
          </div>
        </div>
      )}

      {voidResult && (
        <div className="card" style={{ borderLeft: '4px solid var(--navy)', marginBottom: 16 }}>
          <div className="card-body" style={{ fontSize: 14 }}>
            Void check complete: checked {fmtN(voidResult.checked)} transactions,
            {' '}<strong>{fmtN(voidResult.voided)} voided</strong>.
          </div>
        </div>
      )}

      {syncResult && (
        <div className="card" style={{ borderLeft: '4px solid var(--orange)', marginBottom: 16 }}>
          <div className="card-body" style={{ fontSize: 14 }}>
            Sync complete: <strong>{fmtN(syncResult.totalRows)} renewal transactions</strong> stored.
            {syncResult.summary && (
              <span>
                {' '}Backend reports {fmtN(syncResult.summary.totalSucceeded)} succeeded,
                {' '}{fmtN(syncResult.summary.totalFailed)} failed,
                {' '}{fmt$(syncResult.summary.totalCollected)} collected.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e2e8f0' }}>
        {[
          { id: 'drift', label: 'Drift' },
          { id: 'summary', label: 'Summary' },
          { id: 'duplicates', label: 'Duplicate Charges' },
          { id: 'not-attempted', label: 'Not Attempted' },
          { id: 'transactions', label: 'All Transactions' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: tab === t.id ? '3px solid var(--orange)' : '3px solid transparent',
              background: 'none',
              fontWeight: tab === t.id ? 700 : 400,
              color: tab === t.id ? 'var(--navy)' : '#64748b',
              cursor: 'pointer',
              fontSize: 14,
              fontFamily: 'Montserrat, sans-serif',
            }}
          >
            {t.label}
            {t.id === 'not-attempted' && report?.notAttemptedCount > 0 && (
              <span style={{
                background: '#d69e2e', color: '#fff', borderRadius: 10,
                padding: '1px 7px', fontSize: 11, marginLeft: 6, fontWeight: 700,
              }}>
                {report.notAttemptedCount}
              </span>
            )}
            {t.id === 'duplicates' && report?.multipleChargeCustomers > 0 && (
              <span style={{
                background: '#e53e3e', color: '#fff', borderRadius: 10,
                padding: '1px 7px', fontSize: 11, marginLeft: 6, fontWeight: 700,
              }}>
                {report.multipleChargeCustomers}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading report...</div>
      ) : !report || report.totalTransactions === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
          No data yet. Click "Sync from Backend" to pull renewal results.
        </div>
      ) : (
        <>
          {tab === 'drift' && <DriftTab />}
          {tab === 'summary' && <SummaryTab report={report} />}
          {tab === 'duplicates' && <DuplicatesTab report={report} expanded={expandedDupes} toggle={toggleDupe} />}
          {tab === 'not-attempted' && (
            <NotAttemptedTab
              rows={notAttempted}
              loading={naLoading}
              total={naTotal}
              page={naPage}
              setPage={setNaPage}
              pageSize={PAGE_SIZE}
            />
          )}
          {tab === 'transactions' && (
            <TransactionsTab
              txns={txns}
              loading={txnLoading}
              filter={txnFilter}
              setFilter={handleFilterChange}
              total={txnTotal}
              page={txnPage}
              setPage={setTxnPage}
              pageSize={PAGE_SIZE}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ── Drift Tab ────────────────────────────────────────────────────────── */
/*
 * Do our VIPs and the BigCommerce VIP group agree?
 *
 * WHY THIS IS THE FIRST TAB. The BigCommerce customer group is not a copy of the
 * membership, it is the SWITCH. Two things read it and nothing else: the 8% discount
 * (a BC promotion scoped to the group) and the Rewards Cash earn (skipped outright if
 * the customer is not in the group when the order webhook fires). So a member missing
 * from the group pays full retail AND earns nothing, on every order, and neither loss
 * shows up anywhere else because GroundShippingMembers still says they are a good VIP.
 *
 * The other direction is Greg's rule: nobody in the group who is not an active member.
 * "If they're gonna be in BigCommerce, they need to be on the other side."
 *
 * ⚠️ This calls the function WITH the session token on purpose. Without it the endpoint
 * returns counts only - by design, because the named list is every member and their
 * email address. A signed-out fetch here would render an empty table under a non-zero
 * count, which reads as "nothing to do" rather than "you are not allowed to see this".
 */

function DriftTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [ran, setRan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = getAccessToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch('/.netlify/functions/vip-group-drift', {
        method: 'POST', headers, body: JSON.stringify({ dryRun: true }),
      });
      const j = await res.json();
      if (!res.ok || j.ok === false) throw new Error(j.error || `HTTP ${res.status}`);
      setData(j); setRan(new Date());
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
      Comparing our members against the live BigCommerce group…
    </div>;
  }
  if (err) {
    return <div style={{ padding: 20, background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8 }}>
      <strong style={{ color: '#c53030' }}>Could not run the comparison.</strong>
      <div style={{ marginTop: 6, color: '#742a2a', fontSize: 14 }}>{err}</div>
      <button onClick={load} style={btn}>Try again</button>
    </div>;
  }

  const missing = data.missing || [];
  const extra = data.extra || [];
  // Counts always come back; names only with a valid session. Saying so beats an empty
  // table under a count of 8.
  const named = Array.isArray(data.missing);
  // A member we could not identify is not evidence that both sides agree, so the
  // all-clear has to wait on them too. `unmatchable` is always a count.
  const unverified = data.unmatchable || 0;
  const clean = !data.missing_from_bc && !data.extra_in_bc && !unverified;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <ArrowRightLeft size={18} color="var(--navy)" />
        <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>
          Our members vs the BigCommerce VIP group
        </div>
        <button onClick={load} disabled={loading} style={{ ...btn, marginLeft: 'auto' }}>
          {loading ? 'Checking…' : 'Re-check now'}
        </button>
      </div>

      <p style={{ color: '#475569', fontSize: 13.5, lineHeight: 1.6, margin: '0 0 18px', maxWidth: 780 }}>
        The BigCommerce group is what actually switches the 8% on, and the Rewards Cash earn is
        skipped entirely without it. So a member missing from the group is <strong>paying full
        retail and earning nothing</strong>, on every order, and nothing else reports it.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 22 }}>
        <MiniCard label="Active members (ours)" value={fmtN(data.active_vips)} color="#2b6cb0" />
        <MiniCard label="In the BigCommerce group" value={fmtN(data.bc_group_total)} color="#2b6cb0" />
        <MiniCard label="Missing from the group" value={fmtN(data.missing_from_bc)}
          color={data.missing_from_bc ? '#c53030' : '#2f855a'} />
        <MiniCard label="In the group, not a member" value={fmtN(data.extra_in_bc)}
          color={data.extra_in_bc ? '#c53030' : '#2f855a'} />
        <MiniCard label="Could not check" value={fmtN(unverified)}
          color={unverified ? '#b7791f' : '#2f855a'} />
      </div>

      {clean && (
        <div style={{ padding: 18, background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, color: '#22543d' }}>
          <CheckCircle size={16} style={{ verticalAlign: -3, marginRight: 8 }} />
          Both sides agree. Every active member is in the group, and nobody is in the group without one.
        </div>
      )}

      {unverified > 0 && (
        <div style={{ padding: 14, background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: 8, marginBottom: 18, color: '#744210', fontSize: 14 }}>
          <strong>{unverified} member{unverified === 1 ? '' : 's'} could not be checked.</strong>{' '}
          We hold no BigCommerce id, email or usable phone and name for them, so this says
          nothing about whether they are getting their discount, only that we cannot tell.
        </div>
      )}

      {!named && !clean && (
        <div style={{ padding: 14, background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: 8, marginBottom: 18, color: '#744210', fontSize: 14 }}>
          Counts only — sign in to see who. The named list is every member and their email address,
          so it needs a session.
        </div>
      )}

      {named && missing.length > 0 && (
        <DriftList
          title={`${missing.length} paying member${missing.length === 1 ? '' : 's'} NOT in the BigCommerce group`}
          note="Charged full retail and earning no Rewards Cash on every order. Adding them to the VIP customer group in BigCommerce fixes both."
          tone="#c53030" rows={missing} idLabel="Customer" idKey="cid"
        />
      )}

      {named && extra.length > 0 && (
        <DriftList
          title={`${extra.length} in the BigCommerce group without an active membership`}
          note="Getting 8% off and earning Rewards Cash without paying for it. Either they lapsed and were never removed, or the account should not be in the group at all."
          tone="#b7791f" rows={extra} idLabel="BC id" idKey="bc_id"
        />
      )}

      <p style={{ marginTop: 22, fontSize: 12, color: '#718096', lineHeight: 1.6, maxWidth: 780 }}>
        Group membership is counted from the <strong>BigCommerce API</strong>, not from our mirror —
        the mirror is store-credit-scoped and produces a confidently wrong number.
        Members are matched three ways before any of this is reported: BigCommerce customer id, then
        email, then phone with first and last name.
        {data.matched_by && (
          <> Matched by id {fmtN(data.matched_by.bc_id)}, by email {fmtN(data.matched_by.email)}, by
          phone and name {fmtN(data.matched_by.phone_name)} — that last group are members whose
          BigCommerce account sits on a different email, and an email-only check would have reported
          every one of them as shortchanged.</>
        )}
        {data.truncated && <strong style={{ color: '#c53030' }}> Paging cap hit — this comparison is incomplete.</strong>}
        {ran && <> Checked {fmtTime(ran)}. Runs on its own daily and emails when either list is not empty.</>}
      </p>
    </div>
  );
}

function DriftList({ title, note, tone, rows, idLabel, idKey }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontWeight: 700, color: tone, fontSize: 14.5, marginBottom: 4 }}>{title}</div>
      <div style={{ color: '#475569', fontSize: 13, marginBottom: 10, maxWidth: 760 }}>{note}</div>
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: '#f7fafc', textAlign: 'left' }}>
              <th style={th}>Name</th><th style={th}>Email</th><th style={th}>{idLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={(r[idKey] || '') + i} style={{ borderTop: '1px solid #edf2f7' }}>
                <td style={td}>{r.name || <span style={{ color: '#a0aec0' }}>no name on file</span>}</td>
                <td style={{ ...td, color: '#4a5568' }}>{r.email || ''}</td>
                <td style={{ ...td, color: '#718096', fontVariantNumeric: 'tabular-nums' }}>{r[idKey]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding: '8px 12px', fontWeight: 700, color: '#4a5568', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em' };
const td = { padding: '8px 12px' };
const btn = {
  padding: '7px 14px', border: '1px solid #cbd5e0', borderRadius: 6, background: '#fff',
  color: 'var(--navy)', fontWeight: 600, fontSize: 13, cursor: 'pointer',
  fontFamily: 'Montserrat, sans-serif',
};

/* ── Summary Tab ──────────────────────────────────────────────────────── */

function SummaryTab({ report }) {
  const r = report;

  return (
    <>
      {/* KPI Cards - Row 1: Membership */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KpiCard
          icon={<Users size={20} />}
          label="Active VIPs"
          value={fmtN(r.activeRenewing)}
          color="var(--navy)"
          sub={`${fmtN(r.totalActiveVips)} total (${fmtN(r.activeNotRenewing)} set to not renew)`}
        />
        <KpiCard
          icon={<CheckCircle size={20} />}
          label="2026 Renewed"
          value={fmtN(r.renewedCount)}
          color="#38a169"
          sub={r.eligibleForRenewal > 0 ? `${((r.renewedCount / r.eligibleForRenewal) * 100).toFixed(1)}% of ${fmtN(r.eligibleForRenewal)} eligible` : null}
        />
        <KpiCard
          icon={<XCircle size={20} />}
          label="2026 Failed"
          value={fmtN(r.expiredCount)}
          color="#e53e3e"
          sub="Attempted, no successful charge"
        />
        {r.notAttemptedCount > 0 && (
          <KpiCard
            icon={<AlertTriangle size={20} />}
            label="Not Attempted"
            value={fmtN(r.notAttemptedCount)}
            color="#d69e2e"
            sub="Never ran through renewal"
          />
        )}
        {r.doNotRenewCount > 0 && (
          <KpiCard
            icon={<XCircle size={20} />}
            label="Do Not Renew"
            value={fmtN(r.doNotRenewCount)}
            color="#718096"
            sub="Opted out of auto-renewal"
          />
        )}
        <KpiCard
          icon={<DollarSign size={20} />}
          label="Renewal Revenue"
          value={fmt$(r.totalApprovedRevenue)}
          color="var(--orange)"
        />
      </div>

      {/* KPI Cards - Row 2: Transaction Breakdown */}
      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#64748b', fontWeight: 600 }}>Authorize.net Transactions</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <MiniCard label="Charged" value={fmtN(r.approvedCount - (r.voidedCount || 0))} color="#38a169" />
        <MiniCard label="Declined" value={fmtN(r.declinedCount)} color="#e53e3e" />
        {r.duplicateSubCount > 0 && <MiniCard label="Duplicate Submissions" value={fmtN(r.duplicateSubCount)} color="#805ad5" />}
        {r.voidedCount > 0 && <MiniCard label="Voided" value={fmtN(r.voidedCount)} color="#718096" />}
        {r.preTxnErrorCount > 0 && <MiniCard label="Pre-Transaction Errors" value={fmtN(r.preTxnErrorCount)} color="#d69e2e" sub="Never sent to Auth.net" />}
      </div>

      {/* Error Breakdown */}
      {r.errorBreakdown?.length > 0 && (
        <>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#64748b', fontWeight: 600 }}>Failure Reasons (by customer)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 24 }}>
            {r.errorBreakdown.map(e => (
              <MiniCard key={e.category} label={e.category} value={fmtN(e.customers)} color="#e53e3e" sub={`${fmtN(e.attempts)} attempts`} />
            ))}
          </div>
        </>
      )}

      {/* Charge Analysis */}
      <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#64748b', fontWeight: 600 }}>Charge Analysis (Successful Only)</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#38a169' }}>{fmtN(r.singleChargeCustomers)}</div>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Customers charged once</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#e53e3e' }}>{fmtN(r.multipleChargeCustomers)}</div>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Customers charged multiple times</div>
            {r.duplicateRevenue > 0 && (
              <div style={{ color: '#e53e3e', fontSize: 12, marginTop: 4 }}>
                {fmt$(r.duplicateRevenue)} in extra charges
              </div>
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--navy)' }}>{fmtN(r.uniqueApprovedCustomers)}</div>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Unique customers renewed</div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Duplicates Tab ───────────────────────────────────────────────────── */

function DuplicatesTab({ report, expanded, toggle }) {
  const dupes = report.multipleChargeDetails || [];

  if (dupes.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
        No duplicate charges found.
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fff5f5', borderRadius: 8, border: '1px solid #fed7d7' }}>
        <strong style={{ color: '#e53e3e' }}>{fmtN(dupes.length)} customers</strong> were charged more than once.
        Total extra charges: <strong style={{ color: '#e53e3e' }}>{fmt$(report.duplicateRevenue)}</strong>
      </div>

      <table className="product-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th>Customer</th>
            <th>Email</th>
            <th style={{ textAlign: 'center' }}>Charges</th>
            <th style={{ textAlign: 'right' }}>Total Charged</th>
            <th style={{ textAlign: 'right' }}>Extra</th>
          </tr>
        </thead>
        <tbody>
          {dupes.map(d => {
            const isExpanded = expanded.has(d.customerId);
            const extra = d.totalCharged - (d.transactions[0]?.amount || 0);
            return (
              <React.Fragment key={d.customerId}>
                <tr
                  onClick={() => toggle(d.customerId)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </td>
                  <td style={{ fontWeight: 600 }}>{d.name}</td>
                  <td style={{ fontSize: 13, color: '#64748b' }}>{d.email}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      background: '#e53e3e', color: '#fff', borderRadius: 10,
                      padding: '2px 8px', fontSize: 12, fontWeight: 700,
                    }}>
                      {d.chargeCount}x
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt$(d.totalCharged)}</td>
                  <td style={{ textAlign: 'right', color: '#e53e3e', fontWeight: 600 }}>+{fmt$(extra)}</td>
                </tr>
                {isExpanded && d.transactions.map(t => (
                  <tr key={t.id} style={{ background: '#f7fafc', fontSize: 13 }}>
                    <td></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#718096' }}>
                      Trans: {t.transactionId || 'N/A'}
                    </td>
                    <td style={{ fontSize: 12, color: '#718096' }}>
                      Card: ****{t.cardLast4}
                      {t.isRetry && <span style={{ marginLeft: 6, color: '#d69e2e', fontWeight: 600 }}>RETRY</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <StatusBadge status={t.status} />
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmt$(t.amount)}</td>
                    <td style={{ textAlign: 'right', color: '#718096', fontSize: 12 }}>{fmtTime(t.time)}</td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Transactions Tab ─────────────────────────────────────────────────── */

function TransactionsTab({ txns, loading, filter, setFilter, total, page, setPage, pageSize }) {
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'approved', label: 'Charged' },
          { id: 'failed', label: 'Declined' },
          { id: 'voided', label: 'Voided' },
          { id: 'duplicates', label: 'Duplicates' },
        ].map(f => (
          <button
            key={f.id}
            className={filter === f.id ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => setFilter(f.id)}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            {f.label}
          </button>
        ))}
        <span style={{ color: '#64748b', fontSize: 13, alignSelf: 'center', marginLeft: 8 }}>
          {fmtN(total)} transactions
          {totalPages > 1 && ` (page ${page} of ${totalPages})`}
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>Loading...</div>
      ) : txns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>No transactions found.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="product-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th>Card</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Trans ID</th>
                <th>Error</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {txns.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 500 }}>
                    {t.customer_name}
                    {t.is_retry && (
                      <span style={{
                        background: '#fefcbf', color: '#744210', borderRadius: 4,
                        padding: '1px 5px', fontSize: 10, marginLeft: 6, fontWeight: 600,
                      }}>RETRY</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{t.customer_email}</td>
                  <td style={{ fontSize: 12 }}>****{t.card_last4}</td>
                  <td style={{ textAlign: 'center' }}><StatusBadge status={t.status} authnetStatus={t.authnet_status} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt$(t.amount)}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#718096' }}>{t.transaction_id || '-'}</td>
                  <td style={{ fontSize: 12, color: '#718096', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.error_message || '-'}
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtTime(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ fontSize: 13 }}>Previous</button>
          <span style={{ alignSelf: 'center', fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ fontSize: 13 }}>Next</button>
        </div>
      )}
    </div>
  );
}

/* ── Not Attempted Tab ─────────────────────────────────────────────────── */

function NotAttemptedTab({ rows, loading, total, page, setPage, pageSize }) {
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fffff0', borderRadius: 8, border: '1px solid #fefcbf' }}>
        <strong style={{ color: '#d69e2e' }}>{fmtN(total)} VIP members</strong> expiring 3/31/2026 were never run through the renewal process.
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>All expiring VIPs were attempted.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="product-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th>Customer ID</th>
                <th>Joined</th>
                <th>Expiration</th>
                <th style={{ textAlign: 'center' }}>Has Payment Profile</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.customer_id}>
                  <td style={{ fontWeight: 500 }}>{t.first_name} {t.last_name}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{t.email}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{t.customer_id}</td>
                  <td style={{ fontSize: 12 }}>{t.joined_date ? new Date(t.joined_date).toLocaleDateString() : '-'}</td>
                  <td style={{ fontSize: 12 }}>{t.expiration_date ? new Date(t.expiration_date).toLocaleDateString() : '-'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {t.has_payment_profile
                      ? <span style={{ color: '#38a169', fontWeight: 600 }}>Yes</span>
                      : <span style={{ color: '#e53e3e', fontWeight: 600 }}>No</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ fontSize: 13 }}>Previous</button>
          <span style={{ alignSelf: 'center', fontSize: 13, color: '#64748b' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ fontSize: 13 }}>Next</button>
        </div>
      )}
    </div>
  );
}

/* ── Shared Components ────────────────────────────────────────────────── */

function KpiCard({ icon, label, value, color, sub }) {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ color }}>{icon}</div>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{label}</span>
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{sub}</div>}
      </div>
    </div>
  );
}

function MiniCard({ label, value, color, sub }) {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: '14px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--navy)' }}>{value}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function StatusBadge({ status, authnetStatus }) {
  let bg, text, label;

  if (authnetStatus === 'voided') {
    bg = '#e2e8f0'; text = '#4a5568'; label = 'Voided';
  } else if ((status || '').toLowerCase() === 'success') {
    bg = '#c6f6d5'; text = '#22543d'; label = 'Auth Charged';
  } else if ((status || '').toLowerCase() === 'failed') {
    bg = '#fed7d7'; text = '#742a2a'; label = 'Declined';
  } else {
    bg = '#e2e8f0'; text = '#4a5568'; label = status || 'Unknown';
  }

  return (
    <span style={{
      background: bg, color: text, borderRadius: 10,
      padding: '2px 10px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}
